import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Icon } from "../components/icons";
import { SectionHeading } from "../components/Sections";
import { EnquiryDialog, LeadForm } from "../components/LeadForm";
import OfferSlider from "../components/OfferSlider";
import PartnerStrip from "../components/PartnerStrip";
import { useSettings } from "../context/SettingsContext";
import {
  brand, services, whyChooseUs, howItWorks, stats, testimonials, blogPosts, shortDisclaimer,
} from "../mock/data";
import { ArrowRight, CheckCircle2, ShieldAlert, Quote, Star } from "lucide-react";

function Hero() {
  const { settings } = useSettings();
  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-[#eef7fd] via-white to-[#eefaf3]">
      <div className="absolute inset-0 opacity-[0.05]"
        style={{ backgroundImage: "radial-gradient(circle at 1px 1px, #148a45 1px, transparent 0)", backgroundSize: "26px 26px" }} />
      <div className="container-page relative grid gap-8 py-10 md:py-12 lg:grid-cols-2 lg:items-stretch">
        {/* LEFT: offer slider on top + hero text below */}
        <div className="flex flex-col gap-8 animate-fade-up">
          <OfferSlider embedded />

          <div>
            <span className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1.5 text-xs font-medium text-primary shadow-sm ring-1 ring-primary/15">
              <span className="h-1.5 w-1.5 rounded-full bg-brand-green" /> Loan &amp; Financial Services Facilitation Platform
            </span>
            <h1 className="mt-4 font-heading text-3xl font-extrabold leading-tight md:text-4xl text-foreground">
              Find the Right Loan for <span className="text-primary">Your Financial Needs</span>
            </h1>
            <p className="mt-2 font-heading text-lg font-semibold text-brand-green">{settings.tagline || brand.tagline}</p>
            <p className="mt-3 max-w-xl text-muted-foreground">{settings.subtitle || brand.subtitle}</p>

            <div className="mt-6 flex flex-wrap gap-3">
              <EnquiryDialog trigger={
                <Button size="lg" className="h-12 px-7 text-base font-semibold">
                  Check Eligibility <ArrowRight className="ml-1.5 h-4 w-4" />
                </Button>} />
              <Button asChild size="lg" variant="outline" className="h-12 border-brand-green/40 px-7 text-base text-brand-green hover:bg-brand-green/5">
                <Link to="/loans/personal-loan">Explore Loans</Link>
              </Button>
            </div>

            <p className="mt-5 flex items-start gap-2 text-xs text-muted-foreground max-w-lg">
              <ShieldAlert className="h-4 w-4 shrink-0 mt-0.5 text-primary" />
              Loan approval, interest rate, tenure and other terms are subject to the respective lender's eligibility
              criteria and underwriting policies.
            </p>

            <div className="mt-6 grid grid-cols-4 gap-4 border-t pt-5">
              {stats.map((s) => (
                <div key={s.label}>
                  <div className="font-heading text-base md:text-lg font-bold text-primary">{s.value}</div>
                  <div className="text-[11px] md:text-xs text-muted-foreground">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* RIGHT: form spanning full column height */}
        <div className="relative flex animate-fade-up" style={{ animationDelay: "120ms" }}>
          <Card className="relative z-10 flex w-full flex-col border p-6 shadow-2xl">
            <h3 className="font-heading text-lg font-bold">Get a call back</h3>
            <p className="mt-1 text-sm text-muted-foreground">Submit basic details and our team will assist you.</p>
            <div className="mt-4 flex-1">
              <LeadForm compact />
            </div>
          </Card>
          <div className="absolute -bottom-6 -right-6 hidden h-40 w-40 rounded-3xl bg-brand-green/10 blur-2xl md:block" />
          <div className="absolute -top-6 -left-6 hidden h-32 w-32 rounded-3xl bg-primary/10 blur-2xl md:block" />
        </div>
      </div>
    </section>
  );
}

function TrustStrip() {
  const items = ["Transparent Process", "Need-based Data Collection", "Explicit Consent", "Grievance Redressal"];
  return (
    <div className="border-b bg-secondary/40">
      <div className="container-page flex flex-wrap items-center justify-center gap-x-10 gap-y-3 py-4">
        {items.map((t) => (
          <span key={t} className="flex items-center gap-2 text-sm font-medium text-primary/90">
            <CheckCircle2 className="h-4 w-4 text-primary" /> {t}
          </span>
        ))}
      </div>
    </div>
  );
}

function Services() {
  return (
    <section className="section-pad">
      <div className="container-page">
        <SectionHeading eyebrow="Our Services"
          title="A digital marketplace for your financial needs"
          desc="Compare and apply for suitable loan, insurance and credit products from our authorised partners." />
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {services.map((s) => (
            <Link to={s.to} key={s.title}>
              <Card className="svc-card group relative h-full overflow-hidden border hover:border-primary/40">
                <div className="relative h-44 overflow-hidden">
                  <img src={s.img} alt={s.title}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                  <div className="absolute left-4 top-4 flex h-11 w-11 items-center justify-center rounded-xl bg-white/95 text-primary shadow">
                    <Icon name={s.icon} className="h-5 w-5" />
                  </div>
                  <h3 className="absolute bottom-3 left-4 right-4 font-heading text-lg font-semibold text-white drop-shadow">
                    {s.title}
                  </h3>
                </div>
                <div className="p-5">
                  <p className="text-sm text-muted-foreground">{s.desc}</p>
                  <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-primary">
                    Apply Now <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </span>
                </div>
              </Card>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  return (
    <section className="section-pad bg-secondary/40">
      <div className="container-page">
        <SectionHeading eyebrow="How It Works" title="A simple, transparent 4-step process" />
        <div className="grid gap-6 md:grid-cols-4">
          {howItWorks.map((h, i) => (
            <div key={h.no} className="relative">
              <Card className="h-full border p-6 text-center">
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-primary text-primary-foreground">
                  <Icon name={h.icon} className="h-6 w-6" />
                </div>
                <div className="mt-3 text-xs font-bold uppercase tracking-wider text-primary">Step {h.no}</div>
                <h3 className="mt-1 font-heading text-base font-semibold">{h.title}</h3>
                <p className="mt-1.5 text-sm text-muted-foreground">{h.desc}</p>
              </Card>
              {i < howItWorks.length - 1 && (
                <ArrowRight className="absolute -right-4 top-1/2 hidden h-6 w-6 -translate-y-1/2 text-primary/30 md:block" />
              )}
            </div>
          ))}
        </div>
        <p className="mt-8 text-center text-sm font-medium text-primary">
          Important: Loansupportindia does not guarantee loan approval.
        </p>
      </div>
    </section>
  );
}

function WhyChoose() {
  return (
    <section className="section-pad">
      <div className="container-page grid gap-12 lg:grid-cols-2 lg:items-center">
        <div>
          <SectionHeading center={false} eyebrow="Why Choose Us"
            title="Committed to making India financially healthy"
            desc="Loansupportindia is built for Indians — helping each other grow with responsibility and transparency." />
          <div className="space-y-5">
            {whyChooseUs.map((w) => (
              <div key={w.no} className="flex gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-secondary text-primary">
                  <Icon name={w.icon} className="h-6 w-6" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-heading text-sm font-bold text-primary/40">{w.no}</span>
                    <h3 className="font-heading text-lg font-semibold">{w.title}</h3>
                  </div>
                  <p className="mt-1 text-sm text-muted-foreground">{w.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="relative">
          <img src="https://images.pexels.com/photos/4308013/pexels-photo-4308013.jpeg"
            alt="Couple planning finances" className="rounded-2xl shadow-xl w-full object-cover aspect-[4/3]" />
          <Card className="absolute -bottom-6 -left-6 hidden w-56 border p-4 shadow-xl md:block">
            <p className="text-sm font-semibold text-foreground">End-to-end support</p>
            <p className="mt-1 text-xs text-muted-foreground">Guidance through every step of your application journey.</p>
          </Card>
        </div>
      </div>
    </section>
  );
}

function Testimonials() {
  return (
    <section className="section-pad bg-secondary/40">
      <div className="container-page">
        <SectionHeading eyebrow="Testimonials" title="What our customers say" />
        <div className="grid gap-6 md:grid-cols-3">
          {testimonials.map((t) => (
            <Card key={t.name} className="border p-6">
              <Quote className="h-8 w-8 text-primary/20" />
              <p className="mt-3 text-sm leading-relaxed text-foreground/80">"{t.text}"</p>
              <div className="mt-5 flex items-center gap-1 text-brand-accent">
                {[...Array(5)].map((_, i) => <Star key={i} className="h-4 w-4 fill-current" />)}
              </div>
              <div className="mt-3">
                <div className="font-heading text-sm font-semibold">{t.name}</div>
                <div className="text-xs text-muted-foreground">{t.role}</div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
}

function BlogTeaser() {
  return (
    <section className="section-pad">
      <div className="container-page">
        <SectionHeading eyebrow="Knowledge Centre" title="Learn before you borrow"
          desc="Practical, jargon-free guides on loans, credit and insurance." />
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {blogPosts.map((b) => (
            <Link to={`/blog/${b.slug}`} key={b.slug} className="group">
              <Card className="svc-card h-full overflow-hidden border">
                <div className="aspect-[16/10] overflow-hidden">
                  <img src={b.image} alt={b.title} className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" />
                </div>
                <div className="p-5">
                  <span className="text-xs font-semibold uppercase tracking-wide text-primary">{b.category}</span>
                  <h3 className="mt-2 font-heading text-base font-semibold leading-snug line-clamp-2">{b.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground line-clamp-2">{b.excerpt}</p>
                </div>
              </Card>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function CTA() {
  return (
    <section className="section-pad">
      <div className="container-page">
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-primary to-[#1b6fa8] px-8 py-14 text-center text-white md:px-16">
          <div className="absolute inset-0 opacity-[0.08]"
            style={{ backgroundImage: "radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)", backgroundSize: "24px 24px" }} />
          <div className="relative">
            <h2 className="font-heading text-2xl md:text-3xl font-bold">Ready to find the right loan for you?</h2>
            <p className="mx-auto mt-3 max-w-2xl text-white/75">
              Submit a quick enquiry and our team will guide you. Approval and terms are decided by the lender.
            </p>
            <div className="mt-7 flex justify-center">
              <EnquiryDialog trigger={
                <Button size="lg" className="h-12 bg-white px-8 text-primary hover:bg-white/90 text-base font-semibold">
                  Check Eligibility <ArrowRight className="ml-1.5 h-4 w-4" />
                </Button>} />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <div>
      <Hero />
      <TrustStrip />
      <Services />
      <PartnerStrip />
      <HowItWorks />
      <WhyChoose />
      <Testimonials />
      <BlogTeaser />
      <CTA />
    </div>
  );
}
